import { Component, Inject } from '@angular/core';
import { Customer } from '@features/profile/models/customer';
import { ConsentCampaign } from '@store/consents/models/consent-campaign';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ConsentItem } from '@store/consents/models/consent-item';
import {
  UpdateConsentDecisionPayload,
  UpdateLandingPageConsentDecisionPayload,
} from '@store/consents/models/update-consent-decision-payload';
import { select, Store } from '@ngrx/store';
import { updateConsentDecisionActions } from '@store/consents/actions/update-consent-decision.actions';
import { updateConsentDecisionSelectors } from '@store/consents/selectors/update-consent-decision.selectors';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { BaseComponent } from '@shared/abstract/base-component.abstract';
import { consentListActions } from '@store/consents/actions/consent-list.actions';
import { BaseModel } from '@shared/models/base-model';
import { OrganizationUnit, Person } from '@features/profile/models/organization-unit';
import {
  IConsentDetailsDialogConfig,
  IConsentDetailsResultMode,
} from '@features/profile/components/consent-details-dialog/consent-details-dialog-config';
import { ConsentsService } from '@store/consents/consents.service';
import { BehaviorSubject, combineLatest, MonoTypeOperatorFunction, Observable, of, pipe } from 'rxjs';
import { initOptionsSelectors } from '@store/app/init-options/init-options.selectors';

export interface ConsentDetailsDialogData extends BaseModel {
  mode?: 'edit' | 'preview';
  config?: IConsentDetailsDialogConfig;
  customer?: Customer;
  consentCampaign?: ConsentCampaign;
  consent?: {
    customerId: string;
    categoryCode: string;
    content: string;
    unit: OrganizationUnit;
    person: Person;
    isNameRequired: boolean;
    isEmailRequired: boolean;
    isPhoneRequired: boolean;
  };
  header?: string;
}

export enum CustomerDecisionEnum {
  ACCEPTED = 'CUSTOMER_DECISION_ACCEPTED',
  DENIED = 'CUSTOMER_DECISION_DENIED',
}

@Component({
  selector: 'app-consent-details-dialog',
  templateUrl: './consent-details-dialog.component.html',
  styleUrls: ['./consent-details-dialog.component.scss'],
})
export class ConsentDetailsDialogComponent extends BaseComponent {
  private id$ = new BehaviorSubject<number>(this.dialogData?.consentCampaign?.id ?? 0);
  private isDataFetching$ = new BehaviorSubject<boolean>(false);

  form: UntypedFormGroup;
  mode: 'edit' | 'preview' = 'edit';
  resultPreviewHtmlMap: Record<number, { isFetching: boolean; html: string }> = {};

  readonly customerDecisionEnum = CustomerDecisionEnum;
  readonly resultModeEnum = IConsentDetailsResultMode;

  defaultLang$ = this.store$.select(initOptionsSelectors.locationOptions).pipe(map(({ defaultLang }) => defaultLang));
  success$ = this.store$.pipe(select(updateConsentDecisionSelectors.success));
  loading$: Observable<boolean> = combineLatest([
    this.store$.pipe(select(updateConsentDecisionSelectors.loading)),
    this.isDataFetching$,
  ]).pipe(this.somePositive.bind(this));

  previewHtml$ = this.id$.asObservable().pipe(
    filter(Boolean),
    this.onlyWhenShowHtml(),
    tap(() => this.isDataFetching$.next(true)),
    withLatestFrom(this.defaultLang$),
    switchMap(([id, defaultLang]) => this.consentsService.getConsentCampaignContent(Number(id), defaultLang)),
    catchError(() => of('')),
    tap(() => this.isDataFetching$.next(false))
  );

  get controls() {
    return (this.form.get('consents') as UntypedFormArray).controls ?? [];
  }

  get consents(): ConsentItem[] {
    return this.dialogData?.consentCampaign?.consents;
  }

  get isPersonConsent(): boolean {
    return Boolean(this.dialogData?.consent?.person);
  }

  get dialogConfig(): IConsentDetailsDialogConfig | undefined {
    return this.dialogData?.config;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public dialogData: ConsentDetailsDialogData,
    private fb: UntypedFormBuilder,
    private store$: Store,
    private dialogRef: MatDialogRef<ConsentDetailsDialogComponent>,
    private consentsService: ConsentsService
  ) {
    super();
  }

  ngOnInit(): void {
    this.createResultsPreviewsMap();
    this.createForm();
    this.closeDialogHandler();
    if (this.dialogData.mode) {
      this.mode = this.dialogData.mode;
      this.form.disable();
      if (this.mode === 'edit') {
        (this.form.get('consents') as UntypedFormArray).controls.forEach((control) => {
          control.get('decision').enable();
        });
      }
    }
  }

  onSubmit(): void {
    // @ts-ignore
    this.store$.dispatch(updateConsentDecisionActions.loading({ data: this.dataFactory() }));
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.store$.dispatch(updateConsentDecisionActions.clear());
  }

  onFetchItemHtml(id: number): void {
    if (this.resultPreviewHtmlMap[id].isFetching) {
      return;
    }
    this.resultPreviewHtmlMap[id].isFetching = true;

    this.defaultLang$
      .pipe(
        take(1),
        switchMap((lang) => this.consentsService.getConsentCampaignConsentContent(id, lang)),
        catchError(() => of(''))
      )
      .subscribe((previewHtml: string) => {
        this.resultPreviewHtmlMap[id].html = previewHtml;
        this.resultPreviewHtmlMap[id].isFetching = false;
      });
  }

  private dataFactory(): UpdateConsentDecisionPayload | UpdateLandingPageConsentDecisionPayload {
    if (this.dialogData.consent) {
      const { decision, firstName, surname, email, phone } = this.form.getRawValue().consents[0];
      return {
        customerId: this.dialogData.consent.customerId,
        ConsentDecisions: [
          {
            ConsentCategoryCode: this.dialogData.consent.categoryCode,
            organizationUnitCode: this.dialogData.consent.unit.code,
            Decision: decision === CustomerDecisionEnum.ACCEPTED ? 'Accepted' : 'Denied',
            firstName,
            surname,
            email,
            phone,
          },
        ],
      };
    }
    return {
      customers: [
        {
          id: this.dialogData.consentCampaign.customerId,
          campaigns: [
            {
              id: this.dialogData.consentCampaign.id,
              consents: this.form.getRawValue().consents,
            },
          ],
        },
      ],
    };
  }

  private createForm(): void {
    this.form = this.fb.group({
      consents: this.fb.array([]),
    });

    if (this.consents) {
      this.consents.forEach((elem: ConsentItem) => {
        (this.form.get('consents') as UntypedFormArray).push(
          this.fb.group({
            id: elem.id,
            decision: elem.statusTranslationId,
          })
        );
      });
    } else if (this.isPersonConsent) {
      const unit = this.dialogData.consent.unit;
      const person = this.dialogData.consent.person;
      let decision = person.decision;
      if (decision) {
        decision = decision === 'Accepted' ? CustomerDecisionEnum.ACCEPTED : CustomerDecisionEnum.DENIED;
      }
      (this.form.get('consents') as UntypedFormArray).push(
        this.fb.group({
          id: new UntypedFormControl(unit.id),
          decision: new UntypedFormControl(decision),
          firstName: new UntypedFormControl(
            person.firstName,
            this.dialogData.consent.isNameRequired ? [Validators.required] : undefined
          ),
          surname: new UntypedFormControl(
            person.surname,
            this.dialogData.consent.isNameRequired ? [Validators.required] : undefined
          ),
          email: new UntypedFormControl(
            person.email,
            this.dialogData.consent.isEmailRequired ? [Validators.required] : undefined
          ),
          phone: new UntypedFormControl(
            person.phone,
            this.dialogData.consent.isPhoneRequired ? [Validators.required] : undefined
          ),
        })
      );
    }
  }

  private closeDialogHandler(): void {
    this.success$
      .pipe(
        distinctUntilChanged(),
        filter((success: boolean) => !!success),
        takeUntil(this.destroyed$)
      )
      .subscribe(() => {
        this.store$.dispatch(consentListActions.loading());
        this.dialogRef.close();
      });
  }

  private createResultsPreviewsMap(): void {
    if (this.consents) {
      for (const consent of this.consents) {
        this.resultPreviewHtmlMap[consent.id] = { isFetching: false, html: '' };
      }
    }
  }

  private onlyWhenShowHtml(): MonoTypeOperatorFunction<number> {
    return pipe(filter(() => this.dialogConfig?.showHtml === true));
  }

  private somePositive(source$: Observable<boolean[]>): Observable<boolean> {
    return source$.pipe(map((loadings): boolean => loadings.some(Boolean)));
  }
}
